import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { complianceColor, complianceLabel } from '../components/shared/theme';
import { inputStyle, bigBtn, formOverlay, formContainer, sectionLabel, tableStyle, thStyle, trStyle } from '../lib/styles';

const emptyCompliance = {
  document_type:"CDL", document_name:"", driver_id:"", related_asset_id:"",
  issue_date:"", expiration_date:"", reference_number:"", status:"CURRENT", notes:"",
};

interface ComplianceRoomProps {
  data: any[];
  drivers: any[];
  assets: any[];
  companyId: string | null;
  newCompliance: typeof emptyCompliance;
  setNewCompliance: (v: typeof emptyCompliance) => void;
  showEntryForm: boolean;
  setShowEntryForm: (v: boolean) => void;
  fetchRoomData: () => void;
}

export default function ComplianceRoom({ data, drivers, assets, companyId, newCompliance, setNewCompliance, showEntryForm, setShowEntryForm, fetchRoomData }: ComplianceRoomProps) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const handleSave = async () => {
    if (!companyId) { alert("Company not loaded yet."); return; }
    if (!newCompliance.document_type) { alert("Document type is required."); return; }
    const payload = { company_id:companyId, ...newCompliance, driver_id:newCompliance.driver_id||null, related_asset_id:newCompliance.related_asset_id||null };
    const { error } = await supabase.from("compliance").insert(payload);
    if (error) { alert(`Compliance insert failed: ${error.message}`); return; }
    setShowEntryForm(false);
    setNewCompliance(emptyCompliance);
    fetchRoomData();
  };

  // Build compliance items from drivers + assets + DB records
  const compItems: any[] = [];
  drivers.forEach((d:any) => {
    if (d.cdl_expiration) compItems.push({ id:`d-cdl-${d.id}`, type:'DRIVER CDL', name:d.full_name, doc:'CDL License', expires:d.cdl_expiration, color:complianceColor(d.cdl_expiration), label:complianceLabel(d.cdl_expiration) });
    if (d.dot_physical_expiration) compItems.push({ id:`d-dot-${d.id}`, type:'DRIVER DOT', name:d.full_name, doc:'DOT Physical', expires:d.dot_physical_expiration, color:complianceColor(d.dot_physical_expiration), label:complianceLabel(d.dot_physical_expiration) });
    if (d.drug_test_date) compItems.push({ id:`d-drug-${d.id}`, type:'DRIVER DRUG', name:d.full_name, doc:'Drug Test', expires:d.drug_test_date, color:complianceColor(d.drug_test_date), label:complianceLabel(d.drug_test_date) });
    if (d.mvr_date) compItems.push({ id:`d-mvr-${d.id}`, type:'DRIVER MVR', name:d.full_name, doc:'MVR Review', expires:d.mvr_date, color:complianceColor(d.mvr_date), label:complianceLabel(d.mvr_date) });
  });
  assets.forEach((a:any) => {
    if (a.registration_expiration) compItems.push({ id:`a-reg-${a.id}`, type:'ASSET REG', name:a.unit_number, doc:'Registration', expires:a.registration_expiration, color:complianceColor(a.registration_expiration), label:complianceLabel(a.registration_expiration) });
    if (a.insurance_expiration) compItems.push({ id:`a-ins-${a.id}`, type:'ASSET INS', name:a.unit_number, doc:'Insurance', expires:a.insurance_expiration, color:complianceColor(a.insurance_expiration), label:complianceLabel(a.insurance_expiration) });
    if (a.next_inspection_due) compItems.push({ id:`a-insp-${a.id}`, type:'ASSET INSP', name:a.unit_number, doc:'DOT Inspection', expires:a.next_inspection_due, color:complianceColor(a.next_inspection_due), label:complianceLabel(a.next_inspection_due) });
  });
  data.forEach((item:any) => {
    compItems.push({ id:`db-${item.id}`, type:item.document_type||'DOCUMENT', name:item.document_name||'---', doc:item.document_name||'---', expires:item.expiration_date, color:complianceColor(item.expiration_date), label:complianceLabel(item.expiration_date) });
  });
  compItems.sort((a,b) => (a.expires||'9999') < (b.expires||'9999') ? -1 : 1);
  const critical = compItems.filter(c=>c.color==='#ff4444');
  const warning = compItems.filter(c=>c.color==='#FFD700');

  return (
    <>
      {/* ── CREATE ENTRY FORM ── */}
      {showEntryForm && (
        <div style={formOverlay}>
          <div style={{ ...formContainer, width:"580px", maxHeight:"90vh", overflowY:"auto" }}>
            <h3 style={{ color:"#32CD32", marginTop:0 }}>NEW COMPLIANCE ENTRY</h3>
            <div style={sectionLabel}>DOCUMENT INFO</div>
            <select style={inputStyle as any} value={newCompliance.document_type} onChange={e=>setNewCompliance({...newCompliance,document_type:e.target.value})}>
              {["CDL","DOT Physical","Drug Test","MVR","Insurance","MC Authority","BOC-3","UCR Registration","MCS-150","Annual Inspection","Accident Report","Other"].map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            <input type="text" placeholder="Document Name / Description" style={inputStyle} value={newCompliance.document_name} onChange={e=>setNewCompliance({...newCompliance,document_name:e.target.value})} />
            <select style={inputStyle as any} value={newCompliance.driver_id} onChange={e=>setNewCompliance({...newCompliance,driver_id:e.target.value})}>
              <option value="">Link to Driver (optional)</option>
              {drivers.map(d=><option key={d.id} value={d.id}>{d.full_name}</option>)}
            </select>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <div><div style={{ color:"#777", fontSize:"11px", marginBottom:"4px" }}>Issue Date</div><input type="date" style={inputStyle} value={newCompliance.issue_date} onChange={e=>setNewCompliance({...newCompliance,issue_date:e.target.value})} /></div>
              <div><div style={{ color:"#777", fontSize:"11px", marginBottom:"4px" }}>Expiration Date</div><input type="date" style={inputStyle} value={newCompliance.expiration_date} onChange={e=>setNewCompliance({...newCompliance,expiration_date:e.target.value})} /></div>
            </div>
            <input type="text" placeholder="Reference Number" style={inputStyle} value={newCompliance.reference_number} onChange={e=>setNewCompliance({...newCompliance,reference_number:e.target.value})} />
            <select style={inputStyle as any} value={newCompliance.status} onChange={e=>setNewCompliance({...newCompliance,status:e.target.value})}>
              {["CURRENT","EXPIRING SOON","EXPIRED","PENDING"].map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <textarea placeholder="Notes..." style={{...inputStyle,resize:"vertical"} as any} rows={2} value={newCompliance.notes} onChange={e=>setNewCompliance({...newCompliance,notes:e.target.value})} />
            <div style={{ display:"flex", gap:"10px", marginTop:"10px" }}>
              <button onClick={handleSave} style={bigBtn}>SAVE TO SYSTEM</button>
              <button onClick={()=>setShowEntryForm(false)} style={{...bigBtn,background:"#333",color:"#fff"}}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* ── CRITICAL ALERTS ── */}
      {critical.length > 0 && (
        <div style={{ marginBottom:"20px", padding:"12px 16px", background:"rgba(255,68,68,0.08)", border:"1px solid #ff4444" }}>
          <div style={{ color:"#ff4444", fontSize:"11px", fontWeight:700, letterSpacing:"2px", marginBottom:"8px" }}>CRITICAL — EXPIRED OR OVERDUE ({critical.length})</div>
          {critical.map(c=>(
            <div key={c.id} style={{ display:"flex", gap:"12px", padding:"6px 0", borderBottom:"1px solid #1a1a1a", fontSize:"12px" }}>
              <span style={{ color:"#ff4444", fontWeight:700, width:"130px", flexShrink:0 }}>{c.type}</span>
              <span style={{ color:"#fff", fontWeight:700, width:"180px", flexShrink:0 }}>{c.name}</span>
              <span style={{ color:"#aaa", flex:1 }}>{c.doc}</span>
              <span style={{ color:"#ff4444", fontWeight:700 }}>{c.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── WARNING ALERTS ── */}
      {warning.length > 0 && (
        <div style={{ marginBottom:"20px", padding:"12px 16px", background:"rgba(255,215,0,0.06)", border:"1px solid #FFD700" }}>
          <div style={{ color:"#FFD700", fontSize:"11px", fontWeight:700, letterSpacing:"2px", marginBottom:"8px" }}>WARNING — EXPIRING WITHIN 30 DAYS ({warning.length})</div>
          {warning.map(c=>(
            <div key={c.id} style={{ display:"flex", gap:"12px", padding:"6px 0", borderBottom:"1px solid #1a1a1a", fontSize:"12px" }}>
              <span style={{ color:"#FFD700", fontWeight:700, width:"130px", flexShrink:0 }}>{c.type}</span>
              <span style={{ color:"#fff", fontWeight:700, width:"180px", flexShrink:0 }}>{c.name}</span>
              <span style={{ color:"#aaa", flex:1 }}>{c.doc}</span>
              <span style={{ color:"#FFD700", fontWeight:700 }}>{c.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── ALL COMPLIANCE ITEMS ── */}
      <div style={{ marginBottom:"20px" }}>
        {(()=>{
          const fi: React.CSSProperties = { background:"#0a0a0a", border:"1px solid #2a2a2a", color:"#fff", padding:"6px 10px", fontFamily:"monospace", fontSize:"11px" };
          const q = search.toLowerCase();
          const filtered = compItems.filter(c => {
            if (q && !((c.name||'').toLowerCase().includes(q) || (c.doc||'').toLowerCase().includes(q) || (c.type||'').toLowerCase().includes(q))) return false;
            if (statusFilter === 'EXPIRED' && c.color !== '#ff4444') return false;
            if (statusFilter === 'EXPIRING' && c.color !== '#FFD700') return false;
            if (statusFilter === 'CURRENT' && c.color === '#ff4444') return false;
            return true;
          });
          return (
          <div style={{ display:"flex", gap:"10px", marginBottom:"12px", alignItems:"center" }}>
            <input placeholder="Search doc or name..." value={search} onChange={e=>setSearch(e.target.value)} style={{ ...fi, width:"200px" }} />
            <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={fi as any}>
              {[['ALL','ALL STATUSES'],['CURRENT','CURRENT'],['EXPIRING','EXPIRING SOON'],['EXPIRED','EXPIRED']].map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
            <span style={{ color:"#32CD32", fontSize:"11px", fontWeight:700, marginLeft:"auto" }}>ALL COMPLIANCE ITEMS ({filtered.length})</span>
          </div>
          );
        })()}
        {(()=>{
          const q = search.toLowerCase();
          const filtered = compItems.filter(c => {
            if (q && !((c.name||'').toLowerCase().includes(q) || (c.doc||'').toLowerCase().includes(q) || (c.type||'').toLowerCase().includes(q))) return false;
            if (statusFilter === 'EXPIRED' && c.color !== '#ff4444') return false;
            if (statusFilter === 'EXPIRING' && c.color !== '#FFD700') return false;
            if (statusFilter === 'CURRENT' && c.color === '#ff4444') return false;
            return true;
          });
          return (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>TYPE</th>
              <th style={thStyle}>NAME</th>
              <th style={thStyle}>DOCUMENT</th>
              <th style={thStyle}>EXPIRES</th>
              <th style={thStyle}>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c=>(
              <tr key={c.id} style={trStyle}>
                <td style={{ padding:"8px 12px", color:"#32CD32", fontSize:"10px" }}>{c.type}</td>
                <td style={{ padding:"8px 12px", fontWeight:700 }}>{c.name}</td>
                <td style={{ padding:"8px 12px", color:"#aaa" }}>{c.doc}</td>
                <td style={{ padding:"8px 12px", color:c.color, fontWeight:700 }}>{c.label}</td>
                <td style={{ padding:"8px 12px" }}><span style={{ color:c.color, fontSize:"10px", border:`1px solid ${c.color}`, padding:"2px 6px" }}>{c.color==="#ff4444"?"EXPIRED":c.color==="#FFD700"?"EXPIRING SOON":"CURRENT"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
          );
        })()}
      </div>
    </>
  );
}
